"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { getSsoProviders, isSsoAvailable, startMicrosoftSso, type SsoProviders } from "@/services/authService"

// Uma consulta por carregamento da página: login e signup compartilham o resultado.
let providersRequest: Promise<SsoProviders | null> | null = null

function loadProviders(): Promise<SsoProviders | null> {
  providersRequest ??= getSsoProviders().catch(() => {
    // Sem resposta da API, esconde o SSO em vez de mostrar botões que podem não funcionar.
    providersRequest = null
    return null
  })
  return providersRequest
}

const GOOGLE_PATH =
  "M564 325.8C564 467.3 467.1 568 324 568C186.8 568 76 457.2 76 320C76 182.8 186.8 72 324 72C390.8 72 447 96.5 490.3 136.9L422.8 201.8C334.5 116.6 170.3 180.6 170.3 320C170.3 406.5 239.4 476.6 324 476.6C422.2 476.6 459 406.2 464.8 369.7L324 369.7L324 284.4L560.1 284.4C562.4 297.1 564 309.3 564 325.8z"
const MICROSOFT_PATH =
  "M96 96L310.6 96L310.6 310.6L96 310.6L96 96zM329.4 96L544 96L544 310.6L329.4 310.6L329.4 96zM96 329.4L310.6 329.4L310.6 544L96 544L96 329.4zM329.4 329.4L544 329.4L544 544L329.4 544L329.4 329.4z"
const APPLE_PATH =
  "M447.1 332.7C446.9 296 463.5 268.3 497.1 247.9C478.3 221 449.9 206.2 412.4 203.3C376.9 200.5 338.1 224 323.9 224C308.9 224 274.5 204.3 247.5 204.3C191.7 205.2 132.4 248.8 132.4 337.5C132.4 363.7 137.2 390.8 146.8 418.7C159.6 455.4 205.8 545.4 254 543.9C279.2 543.3 297 526 329.8 526C361.6 526 378.1 543.9 406.2 543.9C454.8 543.2 496.6 461.4 508.8 424.6C443.6 393.9 447.1 334.6 447.1 332.7zM390.5 168.5C417.8 136.1 415.3 106.6 414.5 96C390.4 97.4 362.5 112.4 346.6 130.9C329.1 150.7 318.8 175.2 321 202.8C347.1 204.8 370.9 191.4 390.5 168.5z"

type Props = {
  intent: "login" | "signup"
  /** Texto do divisor, ex.: "ou entre com". */
  label: string
}

/**
 * Divisor + botões de login social. Cada provedor só aparece se a API o habilitar
 * (`/api/auth/providers`), então ligar/desligar é uma configuração do ambiente.
 */
export function SsoButtons({ intent, label }: Props) {
  const [providers, setProviders] = useState<SsoProviders | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    loadProviders().then((p) => active && setProviders(p))
    return () => {
      active = false
    }
  }, [])

  if (!providers) return null

  const items = [
    providers.google && { key: "google", name: "Google", path: GOOGLE_PATH },
    providers.microsoft && { key: "microsoft", name: "Microsoft", path: MICROSOFT_PATH },
    providers.apple && { key: "apple", name: "Apple", path: APPLE_PATH },
  ].filter(Boolean) as { key: string; name: string; path: string }[]

  if (items.length === 0) return null

  const click = (key: string) => {
    setNotice(null)
    if (key !== "microsoft") {
      setNotice("SSO para este provedor ainda nao esta disponivel.")
      return
    }
    if (!isSsoAvailable()) {
      setNotice("Login com Microsoft indisponivel neste ambiente.")
      return
    }
    startMicrosoftSso(intent)
  }

  return (
    <>
      <div className="flex items-center my-6">
        <Separator className="flex-1" />
        <span className="mx-3 text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <Separator className="flex-1" />
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((item) => (
          <Button
            key={item.key}
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-2 cursor-pointer"
            onClick={() => click(item.key)}
            aria-label={`${intent === "signup" ? "Cadastro" : "Login"} com ${item.name}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-4 w-4" aria-hidden="true">
              <path fill="var(--foreground)" d={item.path} />
            </svg>
            {item.name}
          </Button>
        ))}
      </div>

      {notice && <p className="mt-3 text-xs text-muted-foreground text-center">{notice}</p>}
    </>
  )
}
